import { pathKey } from './path-identity.js';
import { HUMAN_BLOCKER_KINDS } from './conversation-objective.js';
export const HUMAN_ACT_LABEL = Object.freeze({
    '2fa': 'Informar um código ou 2FA',
    payment: 'Fazer um pagamento',
    product_decision: 'Decidir sobre o produto',
    owner_evidence: 'Fornecer uma evidência que só você tem',
    objective_activation: 'Ativar um objetivo proposto',
    confirmation: 'Confirmar ou responder ao acompanhamento',
    other: 'Outra ação sua',
});
/** AL.D4/DD.4 — o ato do dono que destrava um ponto (mesma regra do quadro e da chamada ao dono). */
export function humanActKind(item) {
    return item.ownerLabel === 'Proposta · aguarda o objetivo' ? 'objective_activation'
        : item.blockerKind && item.blockerKind in HUMAN_ACT_LABEL ? item.blockerKind
            : item.review?.state === 'waiting_human' ? 'confirmation' : 'other';
}
/**
 * PR.1 (25/09/2026) — a frase do ato quando o executor ainda não escreveu `ownerAction`. A "próxima ação" do relato é
 * escrita pensando no dono ("Dono conclui o cadastro em https://signup.live.com…"); `remaining[0]` é o trabalho
 * interno do terminal ("ler o motivo da recusa da 8c351a4c no ASC web") e só entra se não houver outra coisa.
 */
export function ownerActText(item) {
    const text = item.delivery?.ownerAction ?? item.delivery?.nextAction ?? item.delivery?.remaining[0] ?? item.nextAction ?? item.blockedReason ?? item.review?.waitReason ?? null;
    if (!text?.trim())
        return null;
    const stripped = text.trim().replace(/^(?:o\s+)?(?:dono|donizeti)\s*[:,–-]\s*/iu, '');
    return stripped.charAt(0).toLocaleUpperCase('pt-BR') + stripped.slice(1);
}
/** PR.1 — a tela do ato: a declarada pelo executor ou o primeiro link http(s) da frase que ele escreveu para o dono. */
export function ownerActUrl(item) {
    if (item.delivery?.ownerActionUrl)
        return item.delivery.ownerActionUrl;
    for (const text of [item.delivery?.nextAction, item.review?.condition, item.nextAction]) {
        const match = text?.match(/https?:\/\/[^\s<>"'`]+/u)?.[0]?.replace(/[).,;:!?]+$/u, '');
        if (!match)
            continue;
        try {
            if (['http:', 'https:'].includes(new URL(match).protocol))
                return match;
        }
        catch { /* segue para o próximo texto */ }
    }
    return null;
}
const BOARD_STATES = ['pendente', 'em_andamento', 'no_produto', 'concluido', 'bloqueado'];
const OPEN_STATES = new Set(['pendente', 'em_andamento', 'no_produto', 'bloqueado']);
function clip(text, max) {
    if (!text)
        return null;
    const single = text.replace(/\s+/g, ' ').trim();
    return single.length > max ? `${single.slice(0, max - 1)}…` : single;
}
function messageFields(message) {
    if (!message)
        return {};
    const continues = message.continuesSubmissionId;
    return continues ? { messageClass: message.kind, continuesSubmissionId: continues } : { messageClass: message.kind };
}
export function buildConversationBoard(input) {
    const bySubmission = new Map(input.queue.map(entry => [entry.id, entry]));
    const messageBySubmission = new Map((input.messages ?? []).map(m => [m.submissionId, m]));
    const localDone = new Map(input.points.map(p => [p.id, p.state === 'done']));
    const isDone = (id) => input.isDone?.(id) ?? localDone.get(id) ?? null;
    const autoResumeBySubmission = new Map((input.autoResume ?? []).map(entry => [entry.submissionId, entry]));
    const latestDelegation = new Map();
    for (const delegation of input.delegations) {
        const current = latestDelegation.get(delegation.pointId);
        if (!current || delegation.dispatchedAt > current.dispatchedAt)
            latestDelegation.set(delegation.pointId, delegation);
    }
    const items = input.points.filter(point => point.kind !== 'constraint' && !point.retired).map(point => {
        const submission = bySubmission.get(point.submissionId);
        const delegation = latestDelegation.get(point.id) ?? null;
        const relatedProduct = input.product.filter(item => item.submissionId === point.submissionId);
        const product = relatedProduct.find(item => point.operationId && item.operationIds?.includes(point.operationId))
            ?? (relatedProduct.length === 1 && relatedProduct[0].operationIds === undefined ? relatedProduct[0] : null);
        let state = 'pendente';
        let owner = 'conversa';
        let ownerLabel = 'Conversa';
        let result = clip(point.resultSummary, 600);
        let blockedReason = clip(point.blockedReason, 400);
        let updatedAt = point.updatedAt ?? submission?.submittedAt ?? input.now;
        const projectCwd = point.projectCwd ?? delegation?.cwd;
        // A session named "Geral" is not a project. Identity follows its repository.
        const projectName = projectCwd?.replace(/\\/g, '/').replace(/\/$/, '').split('/').at(-1) || delegation?.projectName || null;
        const productProject = point.state === 'product_pending' || (!delegation && !!product);
        let project = projectName ?? (productProject ? 'TermDeck' : null);
        let nextAction = point.nextAction;
        switch (point.state) {
            case 'done':
                state = 'concluido';
                if (delegation) {
                    owner = 'terminal';
                    ownerLabel = delegation.projectName;
                    result = result ?? clip(delegation.resultSummary, 600);
                    updatedAt = updatedAt > (delegation.finishedAt ?? "") ? updatedAt : delegation.finishedAt ?? updatedAt;
                }
                break;
            case 'blocked':
                state = 'bloqueado';
                owner = point.blockerOwner === 'human' ? 'dono' : delegation ? 'terminal' : 'produto';
                ownerLabel = point.blockerOwner === 'human' ? 'Depende de você' : point.blockerOwner === 'external' ? 'Aguardando serviço externo' : 'Impedimento técnico';
                if (delegation) {
                    project = projectName;
                    result = result ?? clip(delegation.resultSummary, 600);
                }
                break;
            case 'product_pending':
                project = 'TermDeck';
                state = product && ['done'].includes(product.state) ? 'concluido' : product?.state === 'rejected' ? 'bloqueado' : 'no_produto';
                owner = 'produto';
                ownerLabel = 'Produto';
                result = clip(product?.resultSummary, 600) ?? result;
                if (product?.state === 'rejected')
                    blockedReason = blockedReason ?? clip(product.resultSummary, 400) ?? 'Encerrado sem execução pelo Produto';
                if (product)
                    updatedAt = new Date(product.updatedAtMs).toISOString();
                break;
            case 'delegated':
                owner = 'terminal';
                ownerLabel = delegation?.projectName ?? 'Terminal';
                if (delegation?.state === 'dispatched' && delegation.delivery !== 'confirmed') {
                    state = 'pendente';
                    owner = 'produto';
                    ownerLabel = 'Conferindo envio';
                    result = 'O início do trabalho ainda não foi confirmado. A instrução está preservada; o envio não será repetido sem reconciliação.';
                }
                else if (!delegation || delegation.state === 'dispatched')
                    state = 'em_andamento';
                else if (delegation.state === 'lost') {
                    state = 'pendente';
                    owner = 'conversa';
                    ownerLabel = point.review && point.review.state !== 'waiting_human' ? 'Reentrega agendada' : 'Conversa';
                    result = 'O terminal encerrou antes de informar o resultado.';
                    updatedAt = updatedAt > (delegation.finishedAt ?? "") ? updatedAt : delegation.finishedAt ?? updatedAt;
                }
                else {
                    updatedAt = delegation.finishedAt ?? updatedAt;
                    result = clip(delegation.resultSummary, 600);
                    if (delegation.outcome === 'concluido')
                        state = 'concluido';
                    else if (delegation.outcome === 'bloqueado') {
                        state = 'bloqueado';
                        owner = point.blockerOwner === 'human' ? 'dono' : 'terminal';
                        ownerLabel = point.blockerOwner === 'human' ? 'Depende de você' : 'Impedimento do executor';
                        blockedReason = result;
                    }
                    else {
                        state = 'pendente';
                        owner = 'conversa';
                        ownerLabel = 'Conversa';
                    }
                }
                break;
            case 'proposed':
                // AK.6 — tarefa criada pelo Assistente num objetivo ainda proposto: espera o clique do dono no OBJETIVO, não
                // uma delegação. Não é "sem delegação" (AI.2/AI.3): tem dono e motivo.
                state = 'pendente';
                owner = 'dono';
                ownerLabel = 'Proposta · aguarda o objetivo';
                if (point.origin === 'terminal' && input.objectives?.some(o => o.id === point.objectiveId && o.state === 'active')) {
                    owner = 'conversa';
                    ownerLabel = 'Assistente avaliando pré-requisito';
                }
                break;
            case 'waiting_dependency': {
                state = 'pendente';
                owner = 'conversa';
                const open = (point.dependsOn ?? []).filter(id => isDone(id) !== true);
                const blockedDep = open.find(id => input.points.find(p => p.id === id)?.state === 'blocked');
                ownerLabel = blockedDep ? 'Aguardando dependência bloqueada' : 'Aguardando dependência';
                if (blockedDep) {
                    const dep = input.points.find(p => p.id === blockedDep);
                    blockedReason = `Depende de “${clip(dep.summary, 120)}”, bloqueada${dep.blockerOwner === 'human' ? ' — depende de você' : dep.blockerOwner === 'external' ? ' por terceiro' : ' por causa técnica'}.`;
                }
                break;
            }
            default:
                state = submission && ['working', 'queued', 'deferred'].includes(submission.status) ? 'em_andamento' : 'pendente';
                if (delegation && point.nextAction) {
                    owner = 'terminal';
                    ownerLabel = delegation.projectName;
                }
                // AI.1/AI.2 — "Trabalho restante · Conversa" escondia DOIS estados distintos: reentrega agendada pelo
                // engine (delegação perdida) e pedido que a Conversa nunca chegou a delegar. O quadro nomeia cada um,
                // com a hora da próxima ação — e só diz "depende de você" quando a automação esgotou o teto.
                if (state === 'pendente' && !delegation) {
                    const resume = autoResumeBySubmission.get(point.submissionId);
                    if (resume?.exhausted) {
                        owner = 'dono';
                        ownerLabel = 'Depende de você';
                    }
                    else if (resume)
                        ownerLabel = 'Aguardando o Assistente';
                }
                // Delegação PERDIDA com reentrega agendada pelo acompanhamento (AI.1): o ponto volta a `pending`, mas
                // tem dono — o próprio TermDeck, com hora.
                if (state === 'pendente' && delegation?.state === 'lost' && point.review && !['waiting_human', 'done'].includes(point.review.state))
                    ownerLabel = 'Reentrega agendada';
        }
        if (point.state === 'pending' && point.review?.state === 'waiting_human' && point.review.reason === 'redelivery_exhausted') {
            owner = 'dono';
            ownerLabel = 'Depende de você';
            blockedReason = clip(point.review.waitReason, 400);
        }
        if (delegation?.outcome === 'sem_marcador' && ['pending', 'delegated'].includes(point.state)) {
            state = 'pendente';
            owner = 'produto';
            ownerLabel = 'Conferindo resultado';
            result = 'O TermDeck está conferindo o histórico do executor. Nenhum reenvio ou intervenção sua é necessário para essa conferência.';
            blockedReason = null;
        }
        if (effectiveContinuationReason(delegation) && state !== 'concluido') {
            const reasons = {
                paused: 'Continuação preservada; aguarda o fim da pausa ou manutenção.',
                terminal_offline: 'Executor offline. A continuação precisa recuperar o terminal original sem repetir efeitos.',
                terminal_protected: 'A escolha manual, pausa ou decisão pendente do terminal está sendo respeitada.',
                new_instruction: 'Há uma instrução mais recente no terminal. A continuação anterior foi preservada sem interferir.',
                no_progress: 'O executor repetiu o mesmo residual. É necessário rever a abordagem; nenhum envio será repetido em loop.',
                waiting_idle: 'Continuação automática no mesmo terminal assim que ele estiver disponível.',
            };
            nextAction = reasons[effectiveContinuationReason(delegation)] ?? nextAction;
            owner = 'terminal';
            ownerLabel = delegation.projectName;
        }
        let continuation;
        if (state === 'concluido' && delegation?.terminalLifecycle) {
            const lifecycle = delegation.terminalLifecycle;
            const reasons = {
                recurring_operation: 'Uma rotina deste projeto exige que o terminal permaneça disponível.',
                lote_espera: 'O lote deste projeto declara uma espera com prazo (processo em segundo plano); o terminal fica aberto até ela terminar.',
                human_work: 'Há controle, instrução ou rascunho humano a preservar.',
                viewed_by_human: 'O terminal está aberto em uma tela sua; o encerramento automático aguarda para preservar seu uso e rascunhos.',
                terminal_protected: 'A sessão está em uso manual, pausada ou aguarda uma decisão.',
                active_cycle: 'Outro trabalho ainda está em andamento neste terminal.',
                active_work: 'Há atividade recente; o terminal será preservado.',
                paused: 'Aguarda o fim da pausa ou manutenção.',
                work_remaining: 'Outro apontamento neste terminal ainda não foi concluído.',
                newer_human_instruction: 'Uma instrução mais recente mantém o terminal aberto.',
            };
            continuation = lifecycle.state === 'closed'
                ? { state: 'closed', label: 'Terminal fechado · contexto preservado', detail: 'A próxima abertura retoma esta mesma sessão. Nenhuma ação sua é necessária para encerrar esta entrega.' }
                : lifecycle.state === 'waiting'
                    ? { state: 'blocked', label: 'Terminal mantido aberto', detail: reasons[lifecycle.reason] ?? 'Aguardando comprovação de fim do turno e retomada segura antes de fechar.' }
                    : { state: 'reconciliation', label: 'Conferindo encerramento', detail: 'O contexto foi preservado. O TermDeck está confirmando a saída, sem repetir o fechamento.' };
        }
        if (state === 'pendente' && point.state === 'pending' && !delegation && !product) {
            const resume = autoResumeBySubmission.get(point.submissionId);
            if (resume?.exhausted)
                continuation = { state: 'blocked', label: 'Precisa do Assistente', detail: `O TermDeck retomou o pedido ${resume.attempts} vez(es) e este apontamento continua sem delegação. Veja a última resposta do Assistente e envie uma mensagem para encaminhá-lo.` };
            else if (resume?.nextAt)
                continuation = { state: 'queued', label: 'Retomada automática agendada', detail: `O Assistente retoma este pedido sozinho às ${new Date(resume.nextAt).toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' })} (Brasília) para delegar o que ficou sem executor. Você não precisa reenviar.` };
        }
        if (state !== 'concluido' && delegation?.state === 'lost' && point.state === 'pending' && point.review && !['waiting_human', 'done'].includes(point.review.state)) {
            const review = point.review;
            // 19/09/2026 — "reenvia às HH:MM" só é verdade para uma hora FUTURA de agenda livre. O Bug Bounty mostrou
            // "às 00:15" por 1h35 (a conferência esperava o terminal ficar ocioso) e "às 01:19" enquanto a 12ª reentrega
            // acabava de ser recusada. Hora vencida ou espera pelo terminal viram o motivo real; recusa repetida traz a
            // contagem e a causa — o dono lê o que está acontecendo, não uma promessa que já falhou.
            const dueMs = review.nextCheckAt ? Date.parse(review.nextCheckAt) : NaN;
            const at = Number.isFinite(dueMs) && dueMs > Date.parse(input.now) ? new Date(dueMs).toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' }) : null;
            const when = at ? ` às ${at} (Brasília)` : ' no próximo ciclo';
            const waitReasons = {
                project_manual: 'O projeto está em modo manual. O Assistente precisa encaminhar o residual respeitando as sessões existentes.',
                project_locked: 'O projeto está travado (arquivado ou sensível): nenhuma automação o abre. Só você abre na tela, ou pede a abertura com a sua fala literal.',
                waiting_idle: 'Aguardando a fronteira livre do terminal; nenhum turno ou envio pendente será interrompido.',
                provider_change_blocked: point.review.waitReason ?? 'A troca para o provedor solicitado aguarda a reconciliação do terminal atual.',
                terminal_release_blocked: point.review.waitReason ?? 'A liberação do terminal aguarda reconciliação.',
                project_busy: 'Outro apontamento deste projeto está sendo entregue; este entra na sequência assim que o terminal liberar.',
                paused: 'A operação está pausada; a reentrega retoma quando a pausa terminar.',
                open_pending: 'O TermDeck está abrindo o terminal do projeto; a instrução original é reenviada assim que ele estiver pronto.',
                multiple_sessions: 'Há mais de um terminal aberto para este projeto; escolha em qual conferir para a reentrega seguir.',
                terminal_protected: 'O terminal do projeto está manual, pausado ou aguardando uma decisão; a reentrega respeita esse controle.',
            };
            continuation = review.state === 'checking'
                ? { state: 'running', label: 'Instrução reentregue ao executor', detail: 'A instrução original foi reenviada ao terminal do projeto; o resultado será atualizado aqui.' }
                : review.reason === 'preflight_refused' && review.attempts > 0
                    ? { state: 'queued', label: `Reentrega recusada ${review.attempts}×`, detail: `${clip(review.waitReason, 400) ?? 'O terminal recusou a instrução antes do envio.'} Nova tentativa${when}. Você não precisa reenviar.` }
                    : review.state === 'waiting_terminal'
                        ? { state: 'queued', label: 'Reentrega aguardando o terminal', detail: `${waitReasons[review.reason] ?? 'A reentrega aguarda o terminal do projeto ficar disponível.'} Você não precisa reenviar.` }
                        : { state: 'queued', label: 'Reentrega agendada', detail: `O TermDeck reenvia a instrução original ao terminal do projeto${when}. Você não precisa reenviar.` };
        }
        else if (state !== 'concluido' && delegation) {
            if (delegation.state === 'dispatched') {
                continuation = delegation.delivery === 'confirmed'
                    ? { state: 'running', label: 'Execução confirmada', detail: 'O executor recebeu o trabalho. O resultado será atualizado aqui.' }
                    : { state: 'reconciliation', label: 'Conferindo envio', detail: 'Aguardando confirmação do envio registrado. Não reenvie o pedido.' };
            }
            else if (delegation.outcome === 'sem_marcador') {
                continuation = { state: 'reconciliation', label: 'Conferindo resultado', detail: 'O histórico do executor está sendo conferido, sem repetir o envio.' };
            }
            else if (point.state === 'pending' || (point.state === 'blocked' && ['technical', 'external'].includes(point.blockerOwner ?? ''))) {
                const ready = delegation.target && delegation.instruction && delegation.actorName && delegation.resultEvidence && delegation.instructionRevision !== undefined
                    && delegation.state === 'finished' && ['pendente', 'bloqueado'].includes(delegation.outcome ?? '');
                const reason = effectiveContinuationReason(delegation);
                const waitingTasks = input.points.filter(p => p.id !== point.id && !p.retired
                    && (p.derivedFrom === point.id || point.dependsOn?.includes(p.id)) && !['done', 'proposed'].includes(p.state));
                const checkMs = point.review?.nextCheckAt ? Date.parse(point.review.nextCheckAt) : NaN;
                const scheduledCheck = point.review?.mode === 'automatic' && point.review.state === 'scheduled'
                    && Number.isFinite(checkMs) && checkMs > Date.parse(input.now);
                const checkAt = scheduledCheck ? new Date(checkMs).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short', timeStyle: 'short' }) : '';
                continuation = point.state === 'pending' && delegation.state === 'finished' && delegation.outcome === 'concluido' && !!delegation.resultEvidence
                    ? point.review?.mode === 'paused'
                        ? { state: 'blocked', label: 'Etapa entregue · acompanhamento pausado', detail: 'A entrega anterior e o trabalho restante estão preservados. O encaminhamento aguarda o fim da pausa deste apontamento.' }
                        : waitingTasks.length
                            ? { state: 'queued', basis: 'linked_tasks', label: 'Etapa entregue · aguardando tarefas vinculadas', detail: `A entrega anterior está preservada. ${waitingTasks.length} tarefa(s) vinculada(s) ainda têm trabalho ou conferência pendente. O Assistente reavalia o restante após os resultados, sem repetir a etapa entregue.` }
                            : { state: 'queued', label: 'Etapa entregue · encaminhando o restante', detail: `A entrega anterior está preservada. ${point.review?.reason === 'project_manual' ? 'O projeto está manual; as sessões existentes serão preservadas. ' : ''}O Assistente precisa distribuir os próximos passos da obrigação vigente. Você não precisa reenviar.` }
                    : !ready
                        ? { state: 'unavailable', label: 'Continuação não agendada', detail: 'O trabalho restante está preservado, mas ainda precisa ser encaminhado com os recibos do pedido original.' }
                        : point.review?.mode === 'paused'
                            ? { state: 'blocked', label: 'Acompanhamento pausado', detail: 'O resultado e o trabalho restante estão preservados. A conferência automática aguarda o fim da pausa.' }
                            : scheduledCheck
                                ? { state: 'queued', label: 'Conferência agendada', detail: `Próxima conferência em ${checkAt} (Brasília). ${clip(point.review?.condition, 500) ?? 'O executor confere a condição e continua somente o que faltar.'} Você não precisa reenviar o pedido.` }
                                : point.blockerOwner === 'external'
                                    ? { state: 'blocked', label: 'Aguardando condição externa', detail: nextAction ?? point.blockedReason ?? 'O executor acompanha a condição externa antes de continuar.' }
                                    : reason && !['waiting_idle', 'paused'].includes(reason)
                                        ? { state: 'blocked', label: 'Continuação preservada', detail: nextAction ?? 'O executor ainda não está disponível.' }
                                        : { state: 'queued', label: reason === 'paused' ? 'Aguardando fim da pausa' : 'Continuação agendada', detail: reason === 'paused' ? nextAction : 'O TermDeck continua no mesmo executor após o fim do turno atual. Você não precisa reenviar o pedido.' };
            }
        }
        if (state !== 'concluido' && delegation?.state === 'lost' && delegation.delivery !== 'rejected') {
            owner = 'terminal';
            ownerLabel = 'Conferindo efeitos anteriores';
            continuation = { state: 'reconciliation', label: 'Reconciliação agendada', detail: 'O executor confere o que já ocorreu antes de continuar somente o trabalho restante. A instrução original não será repetida sem prova de ausência de efeito.' };
        }
        let repair;
        if (state !== 'concluido' && effectiveContinuationReason(delegation)?.startsWith('diagnostic:')) {
            const causes = {
                startup_unconfirmed: 'O terminal ainda não confirmou a inicialização.',
                history_unavailable: 'O histórico do executor está indisponível.',
                history_read_failed: 'A leitura do histórico falhou ou excedeu o prazo.',
                continuity_read_unavailable: 'A identidade da sessão sucessora ainda não pôde ser conferida.',
                result_unconfirmed: 'O executor não devolveu um resultado verificável.',
                redelivery_exhausted: 'As tentativas anteriores não produziram resultado.',
                new_instruction_stale: 'Uma instrução posterior precisa ser reconciliada.',
            };
            owner = 'conversa';
            ownerLabel = 'Recuperação técnica pelo Assistente';
            nextAction = `${causes[delegation.continuationReason.slice(11)] ?? 'Há uma falha no acompanhamento.'} O Assistente acompanha a causa e encaminha o reparo ao projeto responsável, preservando efeitos anteriores.`;
            continuation = { state: 'reconciliation', label: 'Diagnóstico técnico em acompanhamento', detail: nextAction };
            const openRepairs = relatedProduct.filter(p => !['done', 'rejected'].includes(p.state));
            const linkedRepair = product && openRepairs.includes(product) ? product : openRepairs.length === 1 ? openRepairs[0] : undefined;
            if (linkedRepair) {
                const at = new Date(linkedRepair.updatedAtMs).toISOString();
                repair = { itemId: linkedRepair.id, state: linkedRepair.state, sessionId: linkedRepair.deliveredSessionId ?? null, updatedAt: at };
                owner = 'produto';
                ownerLabel = 'Reparo encaminhado ao Produto';
                const phase = linkedRepair.state === 'delivered' ? 'Encaminhamento entregue ao terminal do Produto; conclusão ainda não registrada.'
                    : linkedRepair.state === 'in_progress' ? 'O Produto registrou o reparo em andamento.'
                        : linkedRepair.state === 'acknowledged' ? 'O terminal do Produto reconheceu o pedido; início ainda não registrado.' : 'O reparo está na fila do Produto.';
                nextAction = `${phase} Após a correção, o acompanhamento reconcilia os efeitos anteriores e retoma o trabalho restante. O diagnóstico anterior pertence ao histórico.`;
                continuation = { state: 'reconciliation', label: 'Reparo deste pedido no Produto', detail: nextAction };
                if (at > updatedAt)
                    updatedAt = at;
            }
        }
        // 01/10/2026 (item 76adfb66) — conferência de um impedimento HUMANO tipado (KYC, 2FA, pagamento...) em curso ou
        // recusada antes do envio: o impedimento continua sendo o fato vigente até o executor relatar outra coisa. Antes o
        // claim da conferência punha a tarefa em `delegated` e o quadro mostrava "em andamento · Conversa" (ou "Reentrega
        // agendada" / "Recuperação técnica"), e o bloqueio do dono sumia até a próxima conferência repor. Fica por último para
        // valer sobre os rótulos de reconciliação e diagnóstico; a continuação deles segue explicando o estado da conferência.
        const humanBlockUnderReview = !!delegation?.reviewOf && delegation.outcome === null && (point.state === 'delegated' || point.state === 'pending')
            && point.blockerOwner === 'human' && !!point.blockerKind && HUMAN_BLOCKER_KINDS.has(point.blockerKind);
        if (humanBlockUnderReview) {
            state = 'bloqueado';
            owner = 'dono';
            ownerLabel = 'Depende de você';
            blockedReason = blockedReason ?? clip(point.delivery?.blockerEvidence ?? point.delivery?.ownerAction ?? point.nextAction ?? point.delivery?.nextAction, 400);
            result = delegation.state === 'dispatched'
                ? 'Conferência automática do impedimento em curso no terminal; o impedimento segue registrado até o resultado.'
                : 'Conferência automática reagendada; o impedimento segue registrado.';
        }
        return {
            id: point.id,
            requestSeq: submission?.messageSeq ?? 0,
            submissionId: point.submissionId,
            submittedAt: submission?.submittedAt ?? input.now,
            summary: point.summary,
            state, owner, ownerLabel, project, result, blockedReason, updatedAt,
            projectKey: projectCwd ? pathKey(projectCwd) : productProject ? 'product:term-deck' : 'unassigned',
            ...(projectCwd ? { projectCwd } : {}),
            ...(point.amendments?.length ? { amendments: point.amendments.length } : {}),
            ...(point.relatesTo ? { relatesTo: point.relatesTo } : {}),
            ...(point.objectiveId ? { objectiveId: point.objectiveId } : {}),
            ...(point.origin && point.origin !== 'owner' ? { origin: point.origin } : {}),
            ...(point.derivedFrom ? { derivedFrom: point.derivedFrom } : {}),
            ...(point.supersededBy ? { supersededBy: point.supersededBy } : {}),
            ...(point.dependsOn?.length ? { dependsOn: [...point.dependsOn], waitingOn: point.dependsOn.filter(id => isDone(id) !== true) } : {}),
            ...(point.blockerKind ? { blockerKind: point.blockerKind } : {}),
            ...(point.doneCriterion ? { doneCriterion: point.doneCriterion } : {}),
            ...(point.createdReason ? { createdReason: point.createdReason } : {}),
            ...(point.pathConsult ? { pathConsult: { at: point.pathConsult.at, verdict: point.pathConsult.verdict, handled: !!point.pathConsult.handledAt,
                    alternatives: point.pathConsult.alternatives.map(a => ({ path: a.path, ...(a.cost ? { cost: a.cost } : {}), ...(a.risk ? { risk: a.risk } : {}) })),
                    ...(point.pathConsult.missing ? { missing: point.pathConsult.missing } : {}), ...(point.pathConsult.owner ? { owner: point.pathConsult.owner } : {}) } } : {}),
            ...messageFields(messageBySubmission.get(point.submissionId)),
            target: point.engineTarget ?? point.target, delivery: point.delivery, nextAction, continuation, ...(repair ? { repair } : {}),
            review: point.review ? (({ revision, mode, state, nextCheckAt, condition, waitReason, actionability, reason, attempts, unchanged, lastCheckedAt, sessionId }) => ({ revision, mode, state, nextCheckAt, condition, ...(waitReason ? { waitReason } : {}), ...(actionability ? { actionability } : {}), reason, attempts, unchanged, lastCheckedAt, sessionId, ...(projectCwd ? { projectCwd } : {}) }))(point.review) : undefined,
        };
    }).sort((a, b) => b.requestSeq - a.requestSeq || a.summary.localeCompare(b.summary));
    const counts = Object.fromEntries(BOARD_STATES.map(state => [state, items.filter(item => item.state === state).length]));
    const withPoints = new Set(items.map(item => item.submissionId));
    // AK.6 — objetivos com rótulo humano e quem trava; a ordem põe primeiro o que precisa do dono ou está travado.
    const objectiveLabel = (o) => {
        if (o.state === 'proposed')
            return { label: 'Proposto · ativar com um clique', waitingOn: 'você' };
        if (o.state === 'terminal_done')
            return { label: 'Concluído pelo terminal · aguarda sua confirmação', waitingOn: 'você' };
        if (o.state === 'done')
            return { label: 'Concluído', waitingOn: null };
        if (o.state === 'archived')
            return { label: 'Arquivado', waitingOn: null };
        if (o.openDependencies.length)
            return { label: 'Aguardando outro objetivo', waitingOn: 'dependência' };
        if (o.progress.blockedHuman)
            return { label: 'Aguardando você', waitingOn: 'você' };
        if (o.progress.blocked) {
            const blocked = input.points.filter(p => p.objectiveId === o.id && !p.retired && !p.supersededBy && p.kind !== 'constraint' && p.state === 'blocked');
            if (blocked.length === o.progress.blocked && blocked.every(p => p.blockerOwner === 'external')) {
                if (o.progress.inFlight)
                    return { label: 'Em andamento · parte aguarda condição externa', waitingOn: null };
                const actionable = items.some(item => item.objectiveId === o.id && item.state === 'pendente'
                    && !item.waitingOn?.length && item.continuation?.basis !== 'linked_tasks'
                    && item.continuation?.label !== 'Conferência agendada');
                if (actionable)
                    return { label: 'Trabalho restante · parte aguarda condição externa', waitingOn: 'Assistente' };
                return { label: 'Aguardando condição externa', waitingOn: 'condição externa' };
            }
            return { label: 'Travado · replanejamento com o terminal', waitingOn: 'terminal' };
        }
        if (o.progress.inFlight)
            return { label: 'Em andamento', waitingOn: null };
        if (o.progress.allDone)
            return { label: 'Tarefas concluídas · verificando o critério', waitingOn: 'terminal' };
        const remaining = input.points.filter(p => p.objectiveId === o.id && !p.retired && !p.supersededBy
            && p.kind !== 'constraint' && !['done', 'proposed'].includes(p.state));
        const scheduled = (p) => items.find(item => item.id === p.id)?.continuation?.label === 'Conferência agendada';
        if (remaining.length === o.progress.total - o.progress.done - o.progress.proposed && remaining.some(scheduled)
            && remaining.every(p => scheduled(p) || p.state === 'waiting_dependency'
                || items.find(item => item.id === p.id)?.continuation?.basis === 'linked_tasks')) {
            return { label: 'Acompanhamento agendado', waitingOn: 'conferência' };
        }
        return { label: 'Aguardando encaminhamento', waitingOn: 'Assistente' };
    };
    const rank = (o) => (o.state === 'terminal_done' || o.state === 'proposed' ? 0 : o.progress.blockedHuman ? 1 : o.progress.blocked ? 2 : o.state === 'active' ? 3 : 4);
    const objectives = (input.objectives ?? []).map(o => {
        const projectCwd = o.projects[0] ?? null;
        const { label, waitingOn } = objectiveLabel(o);
        const tasks = items.filter(item => item.objectiveId === o.id);
        const residual = tasks.find(item => item.continuation?.label === 'Etapa entregue · encaminhando o restante');
        const pending = tasks.find(item => item.state !== 'concluido' && item.id !== o.verificationPointId);
        const productionPending = tasks.filter(item => item.state !== 'concluido' && item.delivery?.achieved === 'production').length;
        const context = productionPending ? `${productionPending} ${productionPending === 1 ? 'tarefa em produção aguarda' : 'tarefas em produção aguardam'} aceite. O percentual mede tarefas verificadas. ` : '';
        const explanation = context + (residual
            ? `${o.progress.done} de ${o.progress.total} tarefas completas. Uma etapa anterior já tem prova; falta distribuir ou executar o restante. ${residual.continuation.detail}`
            : pending?.continuation?.detail ?? pending?.nextAction ?? (o.state === 'active' ? 'O progresso conta tarefas concluídas com prova. O Assistente encaminha as etapas restantes.' : ''));
        return { ...o, projectKey: projectCwd ? pathKey(projectCwd) : null, projectName: projectCwd?.replace(/\\/g, '/').replace(/\/$/, '').split('/').at(-1) ?? null, label, waitingOn,
            ...(explanation ? { explanation } : {}), taskIds: tasks.map(item => item.id) };
    }).sort((a, b) => rank(a) - rank(b) || (b.activatedAt ?? b.createdAt).localeCompare(a.activatedAt ?? a.createdAt));
    // AL.D1 (AK.6.11) — métricas de observação da spec §7, derivadas dos stores (pontos, mensagens, delegações).
    const requestPoints = new Map();
    for (const point of input.points)
        if (point.kind !== 'constraint')
            requestPoints.set(point.submissionId, (requestPoints.get(point.submissionId) ?? 0) + 1);
    const assistantPoints = input.points.filter(point => point.origin === 'assistant' && point.kind !== 'constraint');
    const latestRevisionByPoint = new Map();
    const delegationsByPoint = new Map();
    for (const d of input.delegations) {
        delegationsByPoint.set(d.pointId, (delegationsByPoint.get(d.pointId) ?? 0) + 1);
        if (d.instructionRevision !== undefined)
            latestRevisionByPoint.set(d.pointId, Math.max(latestRevisionByPoint.get(d.pointId) ?? 0, d.instructionRevision));
    }
    const orderedDelegations = [...input.delegations].sort((a, b) => a.dispatchedAt.localeCompare(b.dispatchedAt));
    const seenPoint = new Set();
    let redeliveries = 0;
    let staleInstruction = 0;
    for (const d of orderedDelegations) {
        if (seenPoint.has(d.pointId)) {
            redeliveries += 1;
            const latest = latestRevisionByPoint.get(d.pointId);
            if (d.instructionRevision !== undefined && latest !== undefined && d.instructionRevision < latest)
                staleInstruction += 1;
        }
        seenPoint.add(d.pointId);
    }
    const metrics = {
        requests: { withPoints: requestPoints.size, singleTask: [...requestPoints.values()].filter(n => n === 1).length },
        assistantTasks: {
            proposed: assistantPoints.filter(p => p.state === 'proposed' && !p.retired).length,
            accepted: assistantPoints.filter(p => p.state !== 'proposed' && !p.retired).length,
            discarded: assistantPoints.filter(p => p.retired?.kind === 'cancelled').length,
            open: items.filter(item => item.origin === 'assistant' && OPEN_STATES.has(item.state)).length,
        },
        continuations: {
            classified: (input.messages ?? []).filter(m => m.kind === 'continuation' || m.kind === 'hybrid').length,
            separatedByOwner: (input.messages ?? []).filter(m => m.by === 'owner').length,
        },
        redeliveries: { total: redeliveries, staleInstruction },
    };
    // AL.D4 (AG.5) / DD.17 — "Um ato seu destrava N": agrupa os pontos pelo ATO que os destrava. O mesmo ato é o mesmo
    // tipo com a mesma frase declarada pelo executor; sem a frase, cada ponto é o seu próprio ato.
    const actsById = new Map();
    const ownerItems = items.filter(item => item.owner === 'dono' && item.state !== 'concluido')
        .sort((a, b) => a.submittedAt.localeCompare(b.submittedAt) || a.id.localeCompare(b.id));
    for (const item of ownerItems) {
        const kind = humanActKind(item);
        const declared = item.delivery?.ownerAction?.trim().toLocaleLowerCase('pt-BR').replace(/\s+/g, ' ') || null;
        // Sem frase declarada, o mesmo tipo de ato no MESMO projeto é o mesmo ato (Bug Bounty: KYC em dois pontos); em
        // projetos diferentes são atos diferentes (Conta Apple do Mocha × Microsoft da Convoca).
        const id = `${kind}:${declared ?? `project:${item.projectKey ?? item.project ?? item.id}`}`;
        const act = actsById.get(id) ?? { id, kind, label: HUMAN_ACT_LABEL[kind] ?? HUMAN_ACT_LABEL.other, count: 0, pointIds: [], projects: [], step: null };
        act.count += 1;
        act.pointIds.push(item.id);
        if (item.project && !act.projects.includes(item.project))
            act.projects.push(item.project);
        // DD.3 — a frase do executor PARA O DONO vence o texto interno (remaining/nextAction eram jargão: "execução real ponta a
        // ponta aguarda o dono pedir o primeiro software novo…").
        const ownerAction = item.delivery?.ownerAction;
        // A frase declarada passa pelo mesmo acabamento da não declarada (sem "dono:" e com maiúscula): o executor às vezes
        // escreve "autorizar explicitamente…" e a faixa abria em minúscula.
        if (ownerAction && !act.stepForOwner) {
            act.step = clip(ownerActText(item), 200);
            act.stepForOwner = true;
        }
        if (!act.step)
            act.step = clip(ownerActText(item), 200);
        if (!act.actionUrl) {
            const url = ownerActUrl(item);
            if (url)
                act.actionUrl = url;
        }
        actsById.set(id, act);
    }
    // Mais pontos destravados primeiro; empate conserva a ordem do pedido (o dono que espera há mais tempo vem antes).
    const humanActs = [...actsById.values()].sort((a, b) => b.count - a.count);
    return {
        ...(objectives.length ? { objectives } : {}),
        ...(input.messages?.length ? { messages: [...input.messages] } : {}),
        metrics,
        ...(humanActs.length ? { humanActs } : {}),
        topicId: input.topicId,
        constraints: input.points.filter(p => p.kind === 'constraint').map(p => ({ id: p.id, submissionId: p.submissionId, requestSeq: bySubmission.get(p.submissionId)?.messageSeq ?? 0, summary: p.summary, result: p.resultSummary ?? null })),
        retired: input.points.filter(p => p.retired).map(p => ({ id: p.id, submissionId: p.submissionId, requestSeq: bySubmission.get(p.submissionId)?.messageSeq ?? 0,
            summary: p.summary, kind: p.retired.kind, into: p.retired.into ?? null, note: p.retired.note, at: p.retired.at }))
            .sort((a, b) => b.at.localeCompare(a.at)).slice(0, 40),
        generatedAt: input.now,
        items,
        counts,
        open: items.filter(item => OPEN_STATES.has(item.state)).length,
        requestsWithoutPoints: input.queue.filter(entry => !withPoints.has(entry.id) && (entry.text?.trim().length ?? 1) > 0)
            .map(entry => ({ submissionId: entry.id, requestSeq: entry.messageSeq, status: entry.status, submittedAt: entry.submittedAt })),
        activity: [...input.activity].sort((a, b) => b.at.localeCompare(a.at)).slice(0, 80),
    };
}
const DIGEST_ITEM_MAX = 12;
/**
 * Resumo bounded (≈3 KB) do quadro para o próximo turno HUMANO. Nunca dispara turno por si só.
 * `since` recorta as mudanças ocorridas depois do último turno concluído.
 */
export function conversationBoardDigest(board, since) {
    const openItems = board.items.filter(item => OPEN_STATES.has(item.state));
    const objectives = (board.objectives ?? []).filter(o => o.state !== 'done' && o.state !== 'archived');
    const inObjective = new Set(objectives.flatMap(o => o.taskIds));
    const loose = openItems.filter(item => !item.objectiveId || !inObjective.has(item.id)).slice(0, DIGEST_ITEM_MAX);
    const changed = board.activity.filter(entry => !since || entry.at > since).slice(0, DIGEST_ITEM_MAX);
    if (!loose.length && !objectives.length && !changed.length)
        return '';
    const label = { pendente: 'pendente', em_andamento: 'em andamento', no_produto: 'no Produto', concluido: 'concluído', bloqueado: 'bloqueado' };
    const who = (item) => item.owner === 'terminal' ? `terminal ${item.ownerLabel}` : item.owner === 'produto' ? 'Produto' : item.owner === 'dono' ? 'depende do dono' : 'Conversa';
    const lines = ['Estado dos apontamentos anteriores (contexto, nunca pedidos novos; não repita efeitos já confirmados):',
        'ANTES de registrar qualquer ponto: classify_request_message; depois get_conversation_board(onlyOpen) e leia TODOS os pontos não concluídos do projeto/objetivo para emendar/derivar em vez de duplicar. Este resumo é parcial (por objetivo). Use read_conversation_history para fonte/IDs; get_conversation_objectives para o objetivo inteiro; nunca duplique delegação em voo.'];
    // AL.D4 (AG.5) — paridade CO: o Assistente responde "o que me destrava" sem ler o quadro inteiro.
    const acts = board.humanActs ?? [];
    if (acts.length) {
        const total = acts.reduce((sum, act) => sum + act.count, 0);
        lines.push(`DEPENDE DE VOCÊ — ${total} ponto(s) destravado(s) por ${acts.length} ato(s) do dono:`);
        for (const act of acts.slice(0, 6)) {
            lines.push(`- ${act.label} (${act.count}): ${act.pointIds.slice(0, 4).map(id => id.slice(0, 8)).join(', ')}${act.pointIds.length > 4 ? ', …' : ''} · ${act.projects.join(', ') || 'sem projeto'}${act.step ? ` · passo: ${clip(act.step, 120)}` : ''}`);
        }
    }
    // AK.6 (spec §11.5/§11.11) — por objetivo: "objetivo · n/m · próximas · bloqueios", denso o bastante para 20+ tarefas.
    for (const o of objectives.slice(0, 8)) {
        const tasks = board.items.filter(item => item.objectiveId === o.id && OPEN_STATES.has(item.state));
        const blocked = tasks.filter(t => t.state === 'bloqueado');
        const next = tasks.filter(t => t.state !== 'bloqueado').slice(0, 3);
        lines.push(`OBJETIVO ${o.id} · ${o.projectName ?? 'sem projeto'} · "${clip(o.title, 90)}" · ${o.progress.done}/${o.progress.total} · ${o.label}${o.progress.proposed ? ` · ${o.progress.proposed} proposta(s)` : ''}`);
        for (const t of next)
            lines.push(`  - ${t.id.slice(0, 8)} ${clip(t.summary, 110)} — ${label[t.state]} · ${who(t)}${t.origin === 'assistant' ? ' · [Assistente]' : ''}${t.waitingOn?.length ? ` · aguarda ${t.waitingOn.map(id => id.slice(0, 8)).join(',')}` : ''}`);
        for (const t of blocked.slice(0, 2))
            lines.push(`  - ${t.id.slice(0, 8)} ${clip(t.summary, 110)} — bloqueada (${t.blockerKind ?? 'sem tipo'}) · ${who(t)}${t.blockedReason ? ` · ${clip(t.blockedReason, 120)}` : ''}`);
        if (tasks.length > next.length + Math.min(blocked.length, 2))
            lines.push(`  … +${tasks.length - next.length - Math.min(blocked.length, 2)} abertas (get_conversation_objectives)`);
    }
    if (loose.length) {
        lines.push(`Abertos sem objetivo (${openItems.length - openItems.filter(i => i.objectiveId && inObjective.has(i.id)).length}):`);
        for (const item of loose)
            lines.push(`- [pedido ${item.requestSeq} · ${item.project ?? 'Sem projeto'}] ${clip(item.summary, 160)} — ${label[item.state]} · ${who(item)}${item.result ? ` · ${clip(item.result, 180)}` : ''}`);
    }
    if (changed.length) {
        lines.push(`Mudanças desde o último turno${since ? ` (${since})` : ''}:`);
        for (const entry of changed)
            lines.push(`- ${entry.at.slice(0, 16).replace('T', ' ')}Z · ${clip(entry.text, 260)}`);
    }
    return lines.join('\n').slice(0, 3_200);
}
/**
 * 23/09/2026 — diagnósticos de OBSERVAÇÃO (a leitura do histórico falhou, o início não foi confirmado etc.) dizem
 * só que o acompanhamento não conseguiu ver o resultado. Quando a MESMA delegação tem depois o resultado comprovado
 * no histórico nativo, a observação teve sucesso e o diagnóstico está superado. Sem isto o marcador ficava para
 * sempre: o quadro seguia em "Diagnóstico técnico em acompanhamento" e o Assistente era retomado de novo pelo mesmo
 * diagnóstico (pedido 22 da plataforma-disparos: falha de leitura às 13:06Z, resultado entregue às 13:07:22Z).
 */
export const OBSERVATION_DIAGNOSTICS = new Set([
    'diagnostic:history_read_failed', 'diagnostic:history_read_capacity', 'diagnostic:history_unavailable',
    'diagnostic:startup_unconfirmed', 'diagnostic:continuity_read_unavailable', 'diagnostic:result_unconfirmed',
    // 711d164c (17/10/2026) — "reentrega perdida de novo pela mesma causa" é ausência de confirmação observada; o resultado
    // nativo que chega depois (Convoca 92cc6eff: 31 s depois do diagnóstico) a supera e não reacorda o Assistente.
    'diagnostic:redelivery_exhausted',
]);
export function effectiveContinuationReason(delegation) {
    const reason = delegation?.continuationReason;
    if (reason && delegation?.resultEvidence && OBSERVATION_DIAGNOSTICS.has(reason))
        return undefined;
    // 29/09/2026 (Veloa 79a387ae) — início não confirmado deixa de ser causa quando a entrega foi confirmada depois.
    if (reason === 'diagnostic:startup_unconfirmed' && delegation?.delivery === 'confirmed')
        return undefined;
    return reason;
}
export const DELEGATION_RESULT_MARKER = 'RESULTADO_APONTAMENTO';
/** Rodapé que a Conversa acrescenta ao prompt delegado a um terminal; o nonce distingue delegações. */
export function delegationResultInstruction(nonce) {
    return `Ao terminar este trabalho, escreva como ÚLTIMA linha da sua resposta exatamente:\n${DELEGATION_RESULT_MARKER}[${nonce}]: <concluido|pendente|bloqueado> — <resumo objetivo em até 2 frases do que foi feito, do que falta ou do que impede>`;
}
/**
 * Localiza o marcador de resultado de UMA delegação no scrollback. O eco do próprio prompt (que contém as
 * três alternativas separadas por "|") nunca é aceito; vale a última ocorrência com um único estado.
 */
export function parseDelegationResultMarker(text, nonce) {
    const pattern = new RegExp(`${DELEGATION_RESULT_MARKER}\\[${nonce.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]:\\s*<?\\s*(conclu[ií]do|pendente|bloqueado)\\s*>?\\s*[—–-]+\\s*([^\\n]*)`, 'giu');
    let match;
    let last = null;
    while ((match = pattern.exec(text)) !== null) {
        const raw = match[1].toLowerCase().replace('í', 'i');
        const summary = match[2].trim();
        if (/conclu[ií]do\s*\|\s*pendente/i.test(match[0]) || summary.startsWith('<'))
            continue;
        last = { outcome: raw, summary: summary.slice(0, 600) };
    }
    return last;
}
/** Uma resposta por turno: fragmentos consecutivos de streaming da mesma submissão viram uma entrada. */
export function coalesceTranscript(entries, digest) {
    const transcript = [];
    const seqMap = new Map();
    for (const entry of entries) {
        const last = transcript.at(-1);
        if (entry.role === 'assistant' && last?.role === 'assistant' && (last.submissionId ?? null) === (entry.submissionId ?? null)) {
            last.text = `${last.text}${entry.text}`.slice(0, 200_000);
            last.sha256 = digest(last.text);
            last.byteLength = Buffer.byteLength(last.text, 'utf8');
            last.at = entry.at;
            seqMap.set(entry.seq, last.seq);
            continue;
        }
        const next = { ...entry, seq: transcript.length + 1 };
        seqMap.set(entry.seq, next.seq);
        transcript.push(next);
    }
    return { transcript, seqMap };
}
/**
 * CO.2 — prompt enxuto. Com a sessão nativa retomada, o histórico já está no provedor e só viaja o pedido
 * atual + o digest do quadro. Sem retomada (primeira vez, reset, fallback), vai uma janela curta e legível
 * das últimas trocas humano/assistente — nunca o diário bruto, nunca instruções globais.
 */
export function buildLeanTopicPrompt(input) {
    const parts = [];
    if (!input.nativeHistoryResumed) {
        const recent = input.transcript
            .filter(entry => entry.seq >= input.historyStartSeq && (entry.role === 'user' || entry.role === 'assistant'))
            .filter(entry => !(entry.role === 'user' && entry.text === input.currentText))
            .slice(-12);
        if (recent.length) {
            parts.push('Trechos recentes desta conversa (contexto; não são pedidos novos):');
            for (const entry of recent)
                parts.push(`${entry.role === 'user' ? 'Dono' : 'Você'}: ${entry.text.replace(/\s+/g, ' ').trim().slice(0, 1_500)}`);
        }
    }
    if (input.digest)
        parts.push(input.digest);
    if (input.receipts?.length) {
        parts.push('Operações já confirmadas neste mesmo pedido (não repetir):');
        for (const receipt of input.receipts.slice(0, 20))
            parts.push(`- ${receipt.tool}: ${receipt.summary.replace(/\s+/g, ' ').trim().slice(0, 240)}`);
    }
    const retryLabel = !input.retry ? ''
        : input.retryOrigin === 'auto'
            ? ` · RETOMADA AUTOMÁTICA pelo TermDeck${input.retryReason ? ` (${input.retryReason})` : ''}: o dono NÃO reenviou este pedido. Há apontamentos dele ainda sem delegação — delegue-os ou conclua-os agora, sem repetir efeitos confirmados e sem pedir ao dono autorização para continuar o que ele já pediu`
            : ' · retomada pedida pelo dono: conclua só o que ainda falta, sem repetir efeitos confirmados';
    parts.push(`Pedido atual do dono (pedido ${input.requestSeq}${retryLabel}):\n${input.currentText}`);
    return parts.join('\n\n');
}
//# sourceMappingURL=conversation-board.js.map